/**
 * UpdateAvailableToast — banner that appears after a new app version has been
 * installed (by the service worker).  Tapping "Reload" applies the update
 * by messaging `skip-waiting` to the waiting worker, which in turn fires
 * `controllerchange` → full-page reload (see `updateManager.js`).
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowClockwise, Sparkle, X } from '@phosphor-icons/react';
import { applyUpdateAndReload } from '../updateManager';

export default function UpdateAvailableToast() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onAvail = () => setVisible(true);
    window.addEventListener('cs:update-available', onAvail);
    return () => window.removeEventListener('cs:update-available', onAvail);
  }, []);

  const reload = () => {
    setBusy(true);
    try { navigator.vibrate?.(10); } catch { /* noop */ }
    applyUpdateAndReload();
  };

  const later = () => setVisible(false);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed left-3 right-3 z-[70] sm:left-auto sm:right-4 sm:max-w-sm"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
          role="alert"
          aria-live="polite"
          data-testid="update-toast"
        >
          <div
            className="relative overflow-hidden rounded-2xl p-4"
            style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.16) 0%, var(--cs-bg-surface) 40%, var(--cs-bg-surface) 65%, rgba(16,185,129,0.12) 100%)',
              border: '1px solid rgba(16,185,129,0.4)',
              boxShadow: '0 24px 60px -16px rgba(16,185,129,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
              backdropFilter: 'blur(18px) saturate(180%)',
              WebkitBackdropFilter: 'blur(18px) saturate(180%)',
            }}
          >
            <div
              aria-hidden
              className="absolute -top-8 -right-8 w-28 h-28 rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(circle, rgba(16,185,129,0.35), transparent 70%)',
                filter: 'blur(14px)',
              }}
            />
            <div
              aria-hidden
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{ background: 'linear-gradient(90deg, transparent, #10B981, transparent)' }}
            />

            <button
              onClick={later}
              aria-label="Dismiss"
              className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90"
              style={{ background: 'var(--cs-bg-hover)', color: 'var(--cs-text-muted)' }}
              data-testid="update-toast-dismiss"
            >
              <X size={13} weight="bold" />
            </button>

            <div className="relative flex items-start gap-3">
              <div
                className="w-11 h-11 rounded-xl shrink-0 flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  boxShadow: '0 8px 20px -6px rgba(16,185,129,0.6), inset 0 1px 0 rgba(255,255,255,0.18)',
                }}
              >
                <ArrowClockwise size={20} weight="bold" className="text-white" />
              </div>
              <div className="min-w-0 flex-1 pr-5">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Sparkle size={11} weight="fill" style={{ color: '#F59E0B' }} />
                  <span className="text-[9px] font-bold uppercase tracking-[1.5px]" style={{ color: 'var(--cs-text-muted)' }}>
                    Fresh version ready
                  </span>
                </div>
                <div className="text-[15px] font-black leading-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--cs-text)' }}>
                  New Connection Source <br className="sm:hidden" />update is live
                </div>
                <div className="text-[11px] mt-1.5 leading-relaxed" style={{ color: 'var(--cs-text-muted)' }}>
                  One-tap reload to pick up the latest features &amp; fixes.
                </div>
              </div>
            </div>

            <div className="relative mt-3 flex gap-2">
              <button
                onClick={reload}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black transition-all active:scale-[0.97] disabled:opacity-70"
                style={{
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  color: '#FFF',
                  boxShadow: '0 8px 20px -6px rgba(16,185,129,0.55)',
                  fontFamily: 'Outfit, sans-serif',
                  letterSpacing: '0.5px',
                }}
                data-testid="update-toast-reload-btn"
              >
                <ArrowClockwise size={14} weight="bold" className={busy ? 'animate-spin' : ''} />
                {busy ? 'Reloading…' : 'Reload now'}
              </button>
              <button
                onClick={later}
                className="px-3 py-2.5 rounded-xl text-xs font-bold transition-all active:scale-[0.97]"
                style={{
                  background: 'var(--cs-bg-hover)',
                  color: 'var(--cs-text-muted)',
                  border: '1px solid var(--cs-border)',
                }}
                data-testid="update-toast-later-btn"
              >
                Later
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
